import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { listProducts } from '../lib/api';
import { ChevronRight, ShieldCheck, Award, Truck, Star, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { brands, faqs, testimonials } from '../mock';
import ProductCard from '../components/ProductCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Skeleton } from '../components/ui/skeleton';
import SmartImage from '../components/SmartImage';

const SectionHeader = ({ label, title, cta, to }) => (
  <div className="flex items-end justify-between mb-8 md:mb-10 border-b border-neutral-200 pb-4">
    <div>
      <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase font-medium mb-2">{label}</div>
      <h2 className="font-serif text-2xl md:text-4xl text-[#1a1a1a] tracking-tight">{title}</h2>
    </div>
    {cta && (
      <Link to={to} className="hidden md:flex items-center gap-1 text-sm font-medium text-neutral-700 hover:text-[#b8892d] transition-colors uppercase tracking-wider">
        {cta} <ChevronRight size={16}/>
      </Link>
    )}
  </div>
);

const Home = () => {
  const { data: allProducts, error, isLoading } = useSWR('publicProducts', listProducts);

  const productsToShow = useMemo(() => {
    return allProducts || [];
  }, [allProducts]);

  const isQuotaExceeded = error?.code === 'resource-exhausted' || error?.message?.toLowerCase().includes('quota');

  const menProducts = useMemo(() => productsToShow.filter(p => p.gender === 'men'), [productsToShow]);
  const womenProducts = useMemo(() => productsToShow.filter(p => p.gender === 'women'), [productsToShow]);
  const newArrivals = useMemo(() => {
    return [...productsToShow].sort((a, b) => b.id - a.id);
  }, [productsToShow]);

  return (
    <div className="bg-white">
      {isQuotaExceeded && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 p-2 text-center flex items-center justify-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <p className="text-xs font-medium">Daily request limit reached. Our real catalog will be back shortly.</p>
        </div>
      )}

      {/* HERO */}
      <section className="relative bg-gradient-to-b from-[#f9f5eb] via-[#faf6ee] to-[#ffffff] overflow-hidden pt-12 md:pt-16 pb-0">
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <div className="inline-block bg-[#b8892d] text-white text-[11px] md:text-xs tracking-[0.25em] uppercase px-4 py-2 mb-6 font-bold rounded-md shadow-sm">
            MEGA SALE · UP TO 85% OFF
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight text-[#1a1a1a] font-normal tracking-tight max-w-3xl mx-auto">
            Buy Original Luxury <br className="hidden sm:inline" /> Watches Online In India
          </h1>
          <div className="w-16 h-[2px] bg-[#b8892d] mx-auto my-6"></div>
          <p className="text-neutral-700 text-sm md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            Premium Swiss ETA watches from Rolex, Omega, Tag Heuer, Cartier &amp; more. Cash on Delivery available across India with 1-2 Year machine warranty.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <Link to="/shop">
              <Button className="bg-[#111111] hover:bg-[#222222] text-white h-12 px-8 uppercase tracking-widest text-xs font-bold rounded-md shadow-sm">
                Shop Now
              </Button>
            </Link>
            <Link to="/shop">
              <Button variant="outline" className="bg-transparent hover:bg-[#b8892d]/5 border border-[#b8892d] text-[#b8892d] h-12 px-8 uppercase tracking-widest text-xs font-bold rounded-md shadow-sm">
                Explore Collection
              </Button>
            </Link>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4 mb-12">
            <div className="flex items-center gap-3 text-left">
              <ShieldCheck size={24} className="text-[#b8892d] stroke-[1.5]" />
              <div>
                <span className="block font-serif text-sm font-bold text-[#1a1a1a] leading-tight">100%</span>
                <span className="text-neutral-500 text-[11px] uppercase tracking-wider font-medium">Swiss ETA</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-left">
              <Award size={24} className="text-[#b8892d] stroke-[1.5]" />
              <div>
                <span className="block font-serif text-sm font-bold text-[#1a1a1a] leading-tight">10+ Years</span>
                <span className="text-neutral-500 text-[11px] uppercase tracking-wider font-medium">Trusted</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-left">
              <Truck size={24} className="text-[#b8892d] stroke-[1.5]" />
              <div>
                <span className="block font-serif text-sm font-bold text-[#1a1a1a] leading-tight">Cash on</span>
                <span className="text-neutral-500 text-[11px] uppercase tracking-wider font-medium">Delivery</span>
              </div>
            </div>
          </div>
        </div>

        {/* HERO IMAGE */}
        <div className="w-full max-w-5xl mx-auto px-4 mt-4 relative z-10 overflow-hidden">
          <SmartImage 
            src="/luxury_watches_hero.jpg" 
            alt="Original Luxury Watches Lineup" 
            containerClassName="w-full h-auto rounded-t-2xl shadow-2xl border-x border-t border-amber-100/40"
          />
        </div>
      </section>

      {/* BRANDS */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <SectionHeader label="Shop By Brands" title="Featured Watch Brands" cta="View All" to="/shop"/>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {brands.slice(0, 4).map(b => (
            <Link key={b.id} to={`/shop?brand=${b.name}`} className="group relative overflow-hidden bg-neutral-100 aspect-square">
              <SmartImage src={b.image} alt={b.name} className="w-full h-full group-hover:scale-110 transition-transform duration-500"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10"/>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-white z-10">
                <div className="font-serif text-lg md:text-xl mb-0.5">{b.name} Watches</div>
                <div className="text-xs text-[#e5c98d] tracking-wider uppercase">{b.count} Products</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FOR HIM */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <SectionHeader label="Watches For Him" title="Refined. Bold. Timeless." cta="View Products" to="/shop?gender=men"/>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {isLoading ? (
            Array.from({length:4}).map((_,i) => <div key={i}><Skeleton className="h-64 w-full mb-2"/><Skeleton className="h-4 w-3/4 mb-1"/><Skeleton className="h-4 w-1/4"/></div>)
          ) : (
            menProducts.slice(0, 8).map(p => <ProductCard key={p.id} product={p}/>)
          )}
        </div>
      </section>

      {/* PROMO BANNER */}
      <section className="my-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative overflow-hidden bg-[#1a1a1a] text-white py-12 md:py-20 px-6 md:px-12 text-center rounded-lg">
            <div className="max-w-2xl mx-auto">
              <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase mb-3 font-medium">Limited Time</div>
              <h3 className="font-serif text-3xl md:text-5xl leading-tight mb-4">Mega Season Sale</h3>
              <p className="text-neutral-300 mb-8 max-w-lg mx-auto leading-relaxed">
                Enjoy up to 85% off on our finest selection of luxury watches. Swiss ETA movements, sapphire crystals, premium finish.
              </p>
              <Link to="/shop">
                <Button className="bg-[#b8892d] hover:bg-[#a07826] text-white h-12 px-10 uppercase tracking-widest text-xs font-semibold">
                  Shop the Sale
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOR HER */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <SectionHeader label="Watches For Her" title="Elegance In Every Second" cta="View Products" to="/shop?gender=women"/>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {isLoading ? (
            Array.from({length:4}).map((_,i) => <div key={i}><Skeleton className="h-64 w-full mb-2"/><Skeleton className="h-4 w-3/4 mb-1"/><Skeleton className="h-4 w-1/4"/></div>)
          ) : (
            womenProducts.slice(0, 8).map(p => <ProductCard key={p.id} product={p}/>)
          )}
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="bg-neutral-50 py-16 mt-16">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeader label="Just In" title="New Arrivals Of Watches In India" cta="Shop Now" to="/shop"/>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {isLoading ? (
              Array.from({length:4}).map((_,i) => <div key={i}><Skeleton className="h-64 w-full mb-2"/><Skeleton className="h-4 w-3/4 mb-1"/><Skeleton className="h-4 w-1/4"/></div>)
            ) : (
              newArrivals.slice(0, 8).map(p => <ProductCard key={p.id} product={p}/>)
            )}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <SmartImage 
          src="https://images.unsplash.com/photo-1618215650148-e8e61eae521c?crop=entropy&cs=srgb&fm=jpg&w=800&q=85" 
          alt="About Watch Trader" 
          containerClassName="w-full h-96 rounded-md shadow-sm"
        />
        <div className="space-y-5">
          <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase font-semibold">About Us</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] leading-tight">10+ Years of Trust.<br/>Premium Watches at the Right Price.</h2>
          
          <p className="text-neutral-700 leading-relaxed text-sm md:text-base">
            Welcome to Watch Trader, your trusted destination for premium and luxury watches in India. With over 10 years of experience, we are committed to offering stylish, quality-checked watches with reliable movements and excellent value.
          </p>
          
          <p className="text-neutral-700 leading-relaxed text-sm md:text-base">
            Our collection includes premium watches, luxury-style watches, and selected Swiss ETA movement watches, carefully sourced from reputed manufacturers. Every watch is selected with a focus on quality, craftsmanship, performance, and customer satisfaction.
          </p>
          
          <p className="text-neutral-700 leading-relaxed text-sm md:text-base">
            We believe everyone deserves to experience the look and feel of a premium timepiece without an unreasonable price tag. With Pan-India delivery, Cash on Delivery, and warranty support, we make buying premium watches online simple and reliable.
          </p>

          <div className="pt-4 border-t border-neutral-100">
            <p className="font-serif text-lg text-[#b8892d] italic">
              Watch Trader — Premium Watches. Trusted Quality. Timeless Style.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-neutral-50 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase font-medium mb-3">FAQ</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-3">Frequently Asked Questions</h2>
            <p className="text-neutral-600">Some useful information to make your shopping experience better.</p>
          </div>
          <Accordion type="single" collapsible className="bg-white border border-neutral-200">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-neutral-200 last:border-b-0">
                <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:text-[#b8892d] font-medium">{f.q}</AccordionTrigger>
                <AccordionContent className="px-5 pb-4 text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <div className="text-[#b8892d] text-xs tracking-[0.3em] uppercase font-medium mb-3">Reviews</div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#1a1a1a] mb-3">What Our Customers Say</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">Real stories from real customers across India.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map(t => (
            <div key={t.id} className="bg-white border border-neutral-200 p-6 hover:border-[#b8892d] transition-colors hover:shadow-lg">
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.rating }).map((_, i) => <Star key={`s-${t.id}-${i}`} size={14} className="fill-[#b8892d] text-[#b8892d]"/>)}
              </div>
              <p className="text-neutral-700 leading-relaxed mb-5">“{t.text}”</p>
              <div className="flex items-center gap-3 pt-4 border-t border-neutral-100">
                <div className="w-10 h-10 rounded-full bg-[#b8892d] text-white flex items-center justify-center font-serif">{t.name.charAt(0)}</div>
                <div>
                  <div className="font-medium text-neutral-800 text-sm">{t.name}</div>
                  <div className="text-xs text-neutral-500">{t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
